import { computed, inject, signal, Service } from '@angular/core';
import { PlatformService } from '@core/platform/platform.service';
import { Alert, AlertOptions, AlertType } from './alert.model';

/**
 * AlertService — global, signal-based toast bus for /v2. Fully custom (no toast
 * library): the queue is plain signal state, the {@link AlertHost}
 * renders it, and timing lives here.
 *
 * UX contract:
 * - Toasts STACK (newest on top) up to {@link MAX_VISIBLE}; older overflow is
 *   evicted so the screen never floods.
 * - Each toast owns its own auto-dismiss timer (5s default; pass `durationMs:0`
 *   to make one sticky).
 * - {@link pause}/{@link resume} freeze a toast's countdown while the pointer is
 *   over it (or it holds focus), so a user reading a message never loses it.
 * - {@link dismiss} plays a short exit animation before the toast is removed.
 * - SSR-safe: timers + Date only run in the browser; on the server toasts are
 *   just state and dismissal is immediate.
 */
@Service()
export class AlertService {
  private readonly platform = inject(PlatformService);

  /** Default auto-dismiss for every alert type (ms). */
  private static readonly DEFAULT_DURATION_MS = 5000;
  /** Hard cap on simultaneously-visible toasts (oldest is evicted past this). */
  private static readonly MAX_VISIBLE = 4;
  /** Exit-animation window — MUST stay in sync with the host's leave keyframes. */
  protected static readonly EXIT_MS = 260;

  private seq = 0;
  private readonly _alerts = signal<Alert[]>([]);
  readonly alerts = computed(() => this._alerts());

  /**
   * Per-toast countdown bookkeeping (browser only) — supports pause/resume.
   * `handle` is a DOM `window.setTimeout` id (a number), NOT a Node `Timeout`.
   */
  private readonly timers = new Map<
    number,
    { handle: number | null; remaining: number; startedAt: number }
  >();

  show(type: AlertType, message: string, opts: AlertOptions = {}): number {
    const id = ++this.seq;
    const durationMs = opts.durationMs ?? AlertService.DEFAULT_DURATION_MS;
    const alert: Alert = {
      id,
      type,
      message,
      title: opts.title,
      durationMs,
      onClick: opts.onClick,
      icon: opts.icon,
    };

    // Newest on top; evict the oldest (tail) beyond the visible cap.
    this._alerts.update((list) => {
      const next = [alert, ...list];
      while (next.length > AlertService.MAX_VISIBLE) {
        const dropped = next.pop();
        if (dropped) this.clearTimer(dropped.id);
      }
      return next;
    });

    if (durationMs > 0 && this.platform.isBrowser) this.startTimer(id, durationMs);
    return id;
  }

  success(message: string, opts?: AlertOptions): number {
    return this.show('success', message, opts);
  }
  error(message: string, opts?: AlertOptions): number {
    return this.show('error', message, opts);
  }
  warning(message: string, opts?: AlertOptions): number {
    return this.show('warning', message, opts);
  }
  info(message: string, opts?: AlertOptions): number {
    return this.show('info', message, opts);
  }

  /** Begin dismissal: play the exit animation, then remove (immediate on SSR). */
  dismiss(id: number): void {
    this.clearTimer(id);
    if (!this.platform.isBrowser) {
      this.remove(id);
      return;
    }
    this._alerts.update((list) => list.map((a) => (a.id === id ? { ...a, leaving: true } : a)));
    this.platform.window!.setTimeout(() => this.remove(id), AlertService.EXIT_MS);
  }

  /** Freeze a toast's auto-dismiss countdown (pointer over / focus within). */
  pause(id: number): void {
    const t = this.timers.get(id);
    if (!t || t.handle === null || !this.platform.isBrowser) return;
    this.platform.window!.clearTimeout(t.handle);
    t.remaining = Math.max(0, t.remaining - (Date.now() - t.startedAt));
    t.handle = null;
  }

  /** Resume a paused toast's countdown from where it left off. */
  resume(id: number): void {
    const t = this.timers.get(id);
    if (!t || t.handle !== null || !this.platform.isBrowser) return;
    if (t.remaining <= 0) {
      this.dismiss(id);
      return;
    }
    this.startTimer(id, t.remaining);
  }

  /**
   * Run a tappable toast's action and dismiss it. Called by the host so the
   * "activate, then get out of the way" sequence lives in ONE place, and so a
   * throwing action still dismisses the toast rather than leaving it stuck on
   * screen with a dead tap target.
   */
  activate(id: number): void {
    const alert = this._alerts().find((a) => a.id === id);
    if (!alert?.onClick) return;
    // Already dismissed and playing its exit animation. It is still in the DOM and
    // still clickable for those few hundred ms, so without this a double-tap runs
    // the action TWICE — one push notification navigating twice. The same
    // "one event, one outcome" rule the push de-duplication enforces upstream.
    if (alert.leaving) return;
    try {
      alert.onClick();
    } finally {
      this.dismiss(id);
    }
  }

  clear(): void {
    this.timers.forEach((t) => {
      if (t.handle) this.platform.window!.clearTimeout(t.handle);
    });
    this.timers.clear();
    this._alerts.set([]);
  }

  // ─── internals ──────────────────────────────────────────────────────────
  private startTimer(id: number, ms: number): void {
    const handle = this.platform.window!.setTimeout(() => this.dismiss(id), ms);
    this.timers.set(id, { handle, remaining: ms, startedAt: Date.now() });
  }

  private clearTimer(id: number): void {
    const t = this.timers.get(id);
    if (t?.handle && this.platform.isBrowser) this.platform.window!.clearTimeout(t.handle);
    this.timers.delete(id);
  }

  private remove(id: number): void {
    this.clearTimer(id);
    this._alerts.update((list) => list.filter((a) => a.id !== id));
  }
}
